/**
 * Settings → Rules Engine (V1)
 *
 * Admin-only page for authoring lifecycle automation rules
 * (email notifications and form assignments) for client intake
 * and Episode of Care workflows, backed by PlanDefinition resources.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArchive,
  IconDotsVertical,
  IconPlus,
  IconSettings2,
  IconTrash,
} from '@tabler/icons-react';
import type { UnifyTableColumn } from '@/components/UnifyTable';
import { UnifyTable } from '@/components/UnifyTable';
import { useEffectiveDisplayUserRole } from '@/lib/displayUserRole';

import { useRulesEngine } from './hooks';
import {
  RULE_PACK_DOMAINS,
  TRIGGER_MODES,
  type RulePackDomain,
  type RulePackDraft,
  type RulePackListItem,
} from './types';
import { emptyRulePackDraft, planDefinitionToListItem, planDefinitionToRulePackDraft } from './utils';
import { RulePackEditor } from './components/RulePackEditor';
import { RulePackStatusBadge } from './components/RulePackStatusBadge';

export function RulesEnginePage(): JSX.Element {
  const role = useEffectiveDisplayUserRole();

  if (role !== 'Unify Admin') {
    return (
      <Container size="lg" py="xl">
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Access denied">
          Rules Engine is restricted to Unify Admins.
        </Alert>
      </Container>
    );
  }

  return <RulesEnginePageContent />;
}

type View = { kind: 'list' } | { kind: 'editor'; draft: RulePackDraft };

function RulesEnginePageContent(): JSX.Element {
  const engine = useRulesEngine();
  const [view, setView] = useState<View>({ kind: 'list' });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [categoryModalOpened, setCategoryModalOpened] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RulePackListItem | null>(null);

  const listItems: RulePackListItem[] = useMemo(
    () => engine.data.packs.map(planDefinitionToListItem),
    [engine.data.packs],
  );

  const handleCreate = () => {
    setCategoryModalOpened(true);
  };

  const handleSelectCategory = (domain: RulePackDomain) => {
    setCategoryModalOpened(false);
    const draft = emptyRulePackDraft();
    draft.domain = domain;
    if (domain === 'claim-assembly') {
      draft.trigger.resourceTypes = ['Claim'];
      draft.trigger.operations = ['created'];
      draft.trigger.operation = 'created';
    }
    setView({ kind: 'editor', draft });
  };

  const handleEdit = useCallback(
    (item: RulePackListItem) => {
      const pd = engine.data.packs.find((p) => p.id === item.id);
      if (!pd) return;

      if (pd.status === 'active' || pd.status === 'retired') {
        const draft = engine.editActiveAsDraft(item.id);
        if (draft) {
          setView({ kind: 'editor', draft });
          return;
        }
      }

      setView({ kind: 'editor', draft: planDefinitionToRulePackDraft(pd) });
    },
    [engine],
  );

  const handleBack = () => setView({ kind: 'list' });

  const handleSaveDraft = async (draft: RulePackDraft): Promise<RulePackDraft> => {
    setSaving(true);
    try {
      const saved = await engine.saveDraft(draft);
      const savedDraft = planDefinitionToRulePackDraft(saved);
      setView({ kind: 'editor', draft: savedDraft });
      return savedDraft;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (draft: RulePackDraft): Promise<RulePackDraft> => {
    setPublishing(true);
    try {
      const published = await engine.publish(draft);
      const publishedDraft = planDefinitionToRulePackDraft(published);
      setView({ kind: 'editor', draft: publishedDraft });
      return publishedDraft;
    } finally {
      setPublishing(false);
    }
  };

  const handleRetire = async (id: string) => {
    try {
      await engine.retire(id);
      notifications.show({ title: 'Retired', message: 'Rule has been retired.', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Could not retire rule.', color: 'red' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await engine.deletePack(deleteTarget.id);
      setDeleteTarget(null);
      notifications.show({ title: 'Deleted', message: 'Draft rule has been deleted.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Cannot delete',
        message: err instanceof Error ? err.message : 'Could not delete rule.',
        color: 'red',
      });
    }
  };

  if (engine.loading) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (engine.error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load rules" icon={<IconAlertCircle size={16} />}>
          {engine.error.message}
        </Alert>
      </Container>
    );
  }

  if (view.kind === 'editor') {
    return (
      <RulePackEditor
        initial={view.draft}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        onBack={handleBack}
        saving={saving}
        publishing={publishing}
      />
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <div>
            <Title order={2}>Rules Engine</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Configure automation rules for client intake and Episode of Care workflows.
            </Text>
          </div>
          <Button color="gold" leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            New rule
          </Button>
        </Group>

        <Group gap="md" wrap="wrap">
          <StatBadge
            label="Active"
            count={listItems.filter((i) => i.status === 'active').length}
            color="green"
          />
          <StatBadge
            label="Draft"
            count={listItems.filter((i) => i.status === 'draft').length}
            color="yellow"
          />
          <StatBadge
            label="Retired"
            count={listItems.filter((i) => i.status === 'retired').length}
            color="gray"
          />
        </Group>

        {listItems.length === 0 ? (
          <Paper withBorder p="xl" radius="md">
            <Stack align="center" gap="md">
              <ThemeIcon size={72} radius="xl" color="gray" variant="light">
                <IconSettings2 size={36} />
              </ThemeIcon>
              <Title order={3}>No rules yet</Title>
              <Text size="sm" c="gray.6" maw={420} ta="center">
                Create your first rule to automate client intake or Episode of Care workflows.
              </Text>
              <Button leftSection={<IconPlus size={16} />} color="gold" onClick={handleCreate}>
                New rule
              </Button>
            </Stack>
          </Paper>
        ) : (
          <RulePackTable
            items={listItems}
            onRowClick={handleEdit}
            onRetire={handleRetire}
            onDelete={(item) => setDeleteTarget(item)}
          />
        )}
      </Stack>

      <Modal
        opened={categoryModalOpened}
        onClose={() => setCategoryModalOpened(false)}
        title="What type of rule are you creating?"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Choose a category to continue to the rule editor.
          </Text>
          {RULE_PACK_DOMAINS.map((category) => (
            <Button
              key={category.value}
              variant="outline"
              color="gold"
              fullWidth
              onClick={() => handleSelectCategory(category.value)}
            >
              {category.label}
            </Button>
          ))}
        </Stack>
      </Modal>

      <Modal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete draft rule"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {deleteTarget?.status === 'draft' ? (
              <>Are you sure you want to delete <b>{deleteTarget?.title}</b>? This cannot be undone.</>
            ) : (
              <>Only draft rules can be deleted. Use <b>Retire</b> to deactivate <b>{deleteTarget?.title}</b>.</>
            )}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            {deleteTarget?.status === 'draft' && (
              <Button color="red" onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function RulePackTable({
  items,
  onRowClick,
  onRetire,
  onDelete,
}: {
  items: RulePackListItem[];
  onRowClick: (item: RulePackListItem) => void;
  onRetire: (id: string) => Promise<void>;
  onDelete: (item: RulePackListItem) => void;
}): JSX.Element {
  const columns: UnifyTableColumn<RulePackListItem>[] = useMemo(
    () => [
      {
        key: 'title',
        label: 'Title',
        getValue: (row) => row.title,
        render: (row) => (
          <div>
            <Text size="sm" fw={500}>
              {row.title}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {row.description}
            </Text>
          </div>
        ),
      },
      {
        key: 'domain',
        label: 'Category',
        width: 180,
        getValue: (row) => row.domain,
        render: (row) => (
          <Badge color="gold" variant="light" size="sm">
            {RULE_PACK_DOMAINS.find((d) => d.value === row.domain)?.label ?? row.domain}
          </Badge>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        getValue: (row) => row.status,
        render: (row) => <RulePackStatusBadge status={row.status} />,
      },
      {
        key: 'rungs',
        label: 'Branches',
        width: 80,
        getValue: (row) => String(row.rungCount),
        render: (row) => (
          <Text size="sm" ta="center">
            {row.rungCount}
          </Text>
        ),
      },
      {
        key: 'version',
        label: 'Version',
        width: 80,
        getValue: (row) => row.version,
        render: (row) => (
          <Text size="sm" c="dimmed">
            v{row.version}
          </Text>
        ),
      },
      {
        key: 'triggerMode',
        label: 'Mode',
        width: 120,
        getValue: (row) => row.triggerMode,
        render: (row) => (
          <Badge color="blue" variant="light" size="sm">
            {TRIGGER_MODES.find((m) => m.value === row.triggerMode)?.label ?? row.triggerMode}
          </Badge>
        ),
      },
      {
        key: 'lastUpdated',
        label: 'Last updated',
        width: 160,
        getValue: (row) => row.lastUpdated,
        render: (row) => (
          <Text size="xs" c="dimmed">
            {row.lastUpdated ? new Date(row.lastUpdated).toLocaleDateString() : '—'}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 48,
        pinRight: true,
        getValue: () => '',
        render: (row) => (
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                p={4}
                onClick={(e) => e.stopPropagation()}
              >
                <IconDotsVertical size={14} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
              {row.status === 'active' && (
                <Menu.Item
                  leftSection={<IconArchive size={14} />}
                  onClick={() => onRetire(row.id)}
                >
                  Retire
                </Menu.Item>
              )}
              {row.status === 'draft' && (
                <>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onDelete(row)}
                  >
                    Delete
                  </Menu.Item>
                </>
              )}
              {row.status === 'retired' && (
                <Menu.Item disabled leftSection={<IconArchive size={14} />}>
                  Already retired
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ),
      },
    ],
    [onRetire, onDelete],
  );

  return (
    <UnifyTable
      columns={columns}
      data={items}
      getRowKey={(row) => row.id}
      searchable
      searchPlaceholder="Search rules"
      sortable
      pagination
      defaultRowsPerPage={25}
      onRowClick={onRowClick}
      tableProps={{ highlightOnHover: true }}
      filters={[
        {
          id: 'categoryFilter',
          placeholder: 'Category',
          columnKey: 'domain',
          options: RULE_PACK_DOMAINS.map((d) => ({ value: d.value, label: d.label })),
        },
        {
          id: 'statusFilter',
          placeholder: 'Status',
          columnKey: 'status',
          options: [
            { value: 'active', label: 'Active' },
            { value: 'draft', label: 'Draft' },
            { value: 'retired', label: 'Retired' },
          ],
        },
      ]}
    />
  );
}

function StatBadge({ label, count, color }: { label: string; count: number; color: string }): JSX.Element {
  return (
    <Paper withBorder px="md" py="xs" radius="md">
      <Group gap="xs">
        <Badge
          color={color}
          variant="filled"
          size="sm"
          radius="xl"
          miw={28}
          px={6}
          styles={{ label: { overflow: 'visible', textOverflow: 'unset' } }}
        >
          {count}
        </Badge>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
    </Paper>
  );
}
